import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import {
  Button,
  DateInput,
  Dropdown,
  ResponsiveModalBottomSheet,
  StickyFooter,
  TimeSlotPicker,
} from '@/components';
import type { Availability, Store } from '@/models';
import { BookingService } from '@/services';
import { useBookingStore, useBrandStore } from '@/store';

const SelectDateTime: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const {
    brand,
    setStoreId: setBrandStoreId,
    getMaxAdvanceBookingDays,
    getMaxCustomerCount,
  } = useBrandStore();
  const {
    storeId,
    numberOfGuests,
    availabilityId,
    setStoreId,
    setAvailabilityId,
    setNumberOfGuests,
    clearAllTreatments,
    hasAnyTreatments,
  } = useBookingStore();

  // Helper function to extract time from availability ID
  const extractTimeFromAvailabilityId = (availabilityId: string) =>
    dayjs(availabilityId).format('HH:mm');

  const [localDate, setLocalDate] = useState<Date | null>(
    availabilityId ? new Date(availabilityId) : new Date(),
  );
  const [localTime, setLocalTime] = useState(
    availabilityId ? extractTimeFromAvailabilityId(availabilityId) : '',
  );
  const [selectedAvailabilityId, setSelectedAvailabilityId] =
    useState(availabilityId);

  const [stores, setStores] = useState<Store[]>([]);
  const [availabilities, setAvailabilities] = useState<Availability[]>([]);
  const [loading, setLoading] = useState(false);
  const [showTimeError, setShowTimeError] = useState(false);
  const [showBranchChangeWarning, setShowBranchChangeWarning] = useState(false);
  const [pendingBranchId, setPendingBranchId] = useState<number | null>(null);

  // Handle branch change with warning if needed
  const handleBranchChange = (branchId: number) => {
    // If changing to a different branch and customers have treatments, show warning
    if (branchId !== storeId && hasAnyTreatments()) {
      setPendingBranchId(branchId);
      setShowBranchChangeWarning(true);
    } else {
      // No treatments or same branch, proceed directly
      applyBranchChange(branchId);
    }
  };

  // Apply the branch change
  const applyBranchChange = (branchId: number) => {
    if (branchId > 0) {
      BookingService.setStoreId(branchId);
      setBrandStoreId(branchId);
      setStoreId(branchId);
    }
  };

  // Confirm branch change and clear treatments
  const handleConfirmBranchChange = () => {
    if (pendingBranchId !== null) {
      clearAllTreatments();
      applyBranchChange(pendingBranchId);
      setShowBranchChangeWarning(false);
      setPendingBranchId(null);
    }
  };

  // Cancel branch change
  const handleCancelBranchChange = () => {
    setShowBranchChangeWarning(false);
    setPendingBranchId(null);
  };

  // Load stores from brand data when brand is available
  useEffect(() => {
    if (brand?.branches) {
      setStores(brand.branches);
    }
  }, [brand]);

  // Sync bookingStore's storeId to brandStore on mount if already set
  useEffect(() => {
    if (storeId > 0) {
      setBrandStoreId(storeId);
    }
  }, [storeId, setBrandStoreId]);

  // Initialize store ID based on branch count and existing storeId
  useEffect(() => {
    if (brand?.branches && brand.branches.length > 0) {
      // Case 1: Only one store - auto-select it
      if (brand.branches.length === 1 && storeId === 0) {
        const onlyStore = brand.branches[0];
        BookingService.setStoreId(onlyStore.storeId);
        setBrandStoreId(onlyStore.storeId);
        setStoreId(onlyStore.storeId);
      }
      // Case 2 & 3: Multiple stores handled by user interaction or query param (already set in App.tsx)
    }
  }, [brand, storeId, setBrandStoreId, setStoreId]);

  // Load availabilities when date, branch, or number of guests change
  useEffect(() => {
    if (localDate && storeId > 0) {
      const loadAvailabilities = async () => {
        setLoading(true);
        try {
          // Set the store ID for subsequent API calls
          BookingService.setStoreId(storeId);

          // Convert Date to string format for the API
          const dateString = dayjs(localDate).format('YYYY-MM-DD');

          // Use the new API method that returns Availability[] directly
          const availabilityList = await BookingService.getAvailabilities(
            dateString,
            storeId.toString(),
            numberOfGuests,
          );

          // Filter out past times if selected date is today
          const now = dayjs();
          const isToday = dayjs(localDate).isSame(now, 'day');

          const filteredAvailabilities = isToday
            ? availabilityList.filter((availability) =>
                dayjs(availability.availabilityId).isAfter(now),
              )
            : availabilityList;

          setAvailabilities(filteredAvailabilities);
        } catch (error) {
          console.error('Failed to load availabilities:', error);
        } finally {
          setLoading(false);
        }
      };

      loadAvailabilities();
    }
  }, [localDate, storeId, numberOfGuests]);

  // Check if currently selected time is still available, otherwise reset and auto-select first available
  useEffect(() => {
    if (availabilities.length > 0) {
      // If there's an existing selection, check if the same time is still available
      if (availabilityId) {
        // Extract the time from the previously selected availabilityId
        const previouslySelectedTime =
          extractTimeFromAvailabilityId(availabilityId);

        // Find if the same time exists in the new availability list and is available
        const sameTimeAvailable = availabilities.find(
          (availability) =>
            extractTimeFromAvailabilityId(availability.availabilityId) ===
              previouslySelectedTime && availability.available,
        );

        if (sameTimeAvailable) {
          // Same time is available, update to the new availabilityId (with new date)
          setLocalTime(previouslySelectedTime);
          setSelectedAvailabilityId(sameTimeAvailable.availabilityId);
          setAvailabilityId(sameTimeAvailable.availabilityId);
          return;
        } else {
          // Time is no longer available, reset selection
          setLocalTime('');
          setSelectedAvailabilityId('');
          setAvailabilityId('');
          return;
        }
      }

      // Auto-select first available timeslot only if no existing selection
      if (!availabilityId) {
        const firstAvailable = availabilities.find(
          (availability) => availability.available,
        );
        if (firstAvailable) {
          const timeStr = extractTimeFromAvailabilityId(
            firstAvailable.availabilityId,
          );
          setLocalTime(timeStr);
          setSelectedAvailabilityId(firstAvailable.availabilityId);
          setAvailabilityId(firstAvailable.availabilityId);
        }
      }
    }
  }, [availabilities, availabilityId, setAvailabilityId]);

  // Clamp numberOfGuests if it exceeds max allowed for the selected branch
  useEffect(() => {
    const maxGuests = getMaxCustomerCount();
    if (numberOfGuests > maxGuests) {
      setNumberOfGuests(maxGuests);
    }
  }, [storeId, numberOfGuests, getMaxCustomerCount, setNumberOfGuests]);

  const handleNext = () => {
    if (storeId > 0 && localDate && localTime && selectedAvailabilityId) {
      setShowTimeError(false);
      navigate('/booking/select-treatment');
    } else {
      setShowTimeError(true);
    }
  };

  const renderTimeErrorMessage = () =>
    showTimeError ? (
      <div className="text-error mb-2 text-base leading-5 font-normal">
        {t('validation.timeRequired')}
      </div>
    ) : null;

  return (
    <div className="md:shadow-card mb-20 flex w-full flex-col gap-4 md:mx-auto md:mb-0 md:w-1/2 md:rounded-2xl md:p-6">
      {/* Branch Selection - Only show if there are multiple stores */}
      {stores.length > 1 && (
        <Dropdown
          label={t('dateTime.selectBranch')}
          placeholder={t('dateTime.selectBranch')}
          options={stores.map((store) => ({
            value: store.storeId,
            label: `${store.branchName || store.name}`,
          }))}
          value={storeId > 0 ? storeId : undefined}
          onChange={(value) => {
            const branchId = value as number;
            handleBranchChange(branchId);
          }}
        />
      )}

      <div className="flex flex-col gap-4 md:flex-row">
        <div className="w-full md:w-1/2">
          <Dropdown
            label={t('dateTime.numberOfGuests')}
            placeholder={t('dateTime.numberOfGuests')}
            options={Array.from(
              { length: getMaxCustomerCount() },
              (_, i) => i + 1,
            ).map((num) => ({
              value: num,
              label: num.toString(),
            }))}
            value={numberOfGuests}
            onChange={(value) => setNumberOfGuests(value as number)}
          />
        </div>

        <div className="w-full md:w-1/2">
          <DateInput
            label={t('dateTime.selectDate')}
            value={localDate}
            onChange={(newDate) => {
              setLocalDate(newDate);
            }}
            minDate={new Date()}
            maxDate={dayjs().add(getMaxAdvanceBookingDays(), 'day').toDate()}
          />
        </div>
      </div>

      {/* Time Slots */}
      {localDate && (
        <div className="flex w-full flex-col gap-1">
          <div className="text-secondary-text text-lg leading-7 font-medium">
            {t('dateTime.selectTime')}
          </div>
          <TimeSlotPicker
            availabilities={availabilities}
            selectedAvailabilityId={selectedAvailabilityId}
            loading={loading}
            onSelect={(availability) => {
              const timeStr = extractTimeFromAvailabilityId(
                availability.availabilityId,
              );
              setLocalTime(timeStr);
              setSelectedAvailabilityId(availability.availabilityId);
              setAvailabilityId(availability.availabilityId);
              setShowTimeError(false);
            }}
          />
        </div>
      )}

      {/* Show Next Button only on tablet and up */}
      <div className="mt-2 hidden md:block">
        {renderTimeErrorMessage()}
        <Button onClick={handleNext}>{t('dateTime.cta')}</Button>
      </div>

      {/* Sticky Next Button */}
      <StickyFooter className="md:hidden">
        {renderTimeErrorMessage()}
        <Button onClick={handleNext}>{t('dateTime.cta')}</Button>
      </StickyFooter>

      {/* Branch Change Warning Modal */}
      <ResponsiveModalBottomSheet
        isOpen={showBranchChangeWarning}
        onClose={handleCancelBranchChange}
        showCloseOnMobile={false}
        closable={true}
      >
        <div className="flex flex-col gap-4 px-4 pb-6">
          <div className="flex flex-col gap-4 text-center">
            <p className="text-xl leading-7 font-semibold text-black">
              {t('dateTime.changeBranchWarningTitle')}
            </p>
            <p className="text-secondary-text leading-6">
              {t('dateTime.changeBranchWarningMessage')}
            </p>
          </div>
          <div className="flex gap-4">
            <Button
              variant="secondary"
              onClick={handleCancelBranchChange}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmBranchChange}
              className="flex-1"
            >
              {t('common.confirm')}
            </Button>
          </div>
        </div>
      </ResponsiveModalBottomSheet>
    </div>
  );
};

export default SelectDateTime;
